import {
  Button,
  Flex,
  Text,
  Heading,
  useBreakpointValue,
  Box,
} from "@chakra-ui/react";
import { keyframes } from "@emotion/react";
import { useQuery, skipToken } from "@tanstack/react-query";
import { useTranslation } from "react-i18next";
import { useEffect, useState, useMemo } from "react";
import {
  Artist,
  Bundle,
  Comic,
  ContentSectionCustomCard,
  Release,
  Section,
} from "../../types";
import { useIsLargeBreakpoint } from "../../utils/useBreakpoints.ts";
import { useToggleComicFollow } from "./hooks/useToggleComicFollow.ts";
import { useToggleArtistFollow } from "./hooks/useToggleArtistFollow.ts";
import { useNavigate } from "react-router";
import { CONTEXT_ENTITY_TYPES } from "./constants.ts";
import { ReadingStatusButton } from "../readingStatusButton/ReadingStatusButton.tsx";
import { useAccountData } from "../../hooks/useAccountData.ts";
import { useNavigateToReaderWithContext } from "../../screens/reader/hooks/useNavigateToReaderWithContext.ts";
import { NavigationTrigger } from "../../screens/reader/types.ts";
import { ReleasePriceLabel } from "./ReleasePriceLabel.tsx";
import { useSlideInMenu } from "../slideInMenu/hooks/useSlideInMenu.ts";
import { useReleasePricingInfo } from "../../hooks/useReleasePricingInfo.ts";
import { ReleasePricingInfo, ReleasePricingType } from "../../types.ts";
import { PriceDisplay } from "./components/PriceDisplay.tsx";
import { useBundlePricing } from "../../hooks/useBundlePricing.ts";
import { trackRecsysCardItemClick } from "../../analytics/recsys/tracking.ts";
import { applyNavigationAnalyticsAttribution } from "../../analytics/params/navigationContextAttribution.ts";
import { TFunction } from "i18next";

const fadeIn = keyframes`
  from { opacity: 0; }
  to { opacity: 1; }
`;

const fadeOut = keyframes`
  from { opacity: 1; }
  to { opacity: 0; }
`;

interface HeroCardContentProps {
  content: ContentSectionCustomCard;
  index?: number;
  sectionIndex?: number;
  section?: Section;
  extraPadding?: boolean;
}

export function HeroCardContent({
  content,
  index,
  sectionIndex,
  section,
  extraPadding,
}: HeroCardContentProps) {
  const isLarge = useIsLargeBreakpoint();
  const [isFadingOut, setIsFadingOut] = useState(false);
  const [currentContent, setCurrentContent] = useState(content);
  const { t } = useTranslation();

  useEffect(() => {
    if (content.id !== currentContent.id) {
      setIsFadingOut(true);
      const timer = setTimeout(() => {
        setCurrentContent(content);
        setIsFadingOut(false);
      }, 300);
      return () => clearTimeout(timer);
    }
  }, [content, currentContent]);

  const customCardCacheKey = `${currentContent.entity_type}-${currentContent.id}`;
  const artistCacheKey = `${currentContent.artist?.entity_type}-${currentContent.artist?.id}`;
  const comicCacheKey = `${currentContent.comic?.entity_type}-${currentContent.comic?.id}`;
  const releaseCacheKey = `${currentContent.release?.entity_type}-${currentContent.release?.id}`;
  const bundleCacheKey = `${currentContent.bundle?.entity_type}-${currentContent.bundle?.id}`;

  const { data: customCardData } = useQuery<ContentSectionCustomCard>({
    queryKey: [customCardCacheKey],
    queryFn: skipToken,
    networkMode: "offlineFirst",
    staleTime: Infinity,
  });

  const { data: artistData } = useQuery<Artist>({
    enabled: !!currentContent.artist,
    queryKey: [artistCacheKey],
    queryFn: skipToken,
    networkMode: "offlineFirst",
    staleTime: Infinity,
  });

  const { data: comicData } = useQuery<Comic>({
    enabled: !!currentContent.comic,
    queryKey: [comicCacheKey],
    queryFn: skipToken,
    networkMode: "offlineFirst",
    staleTime: Infinity,
  });

  const { data: releaseData } = useQuery<Release>({
    enabled: !!currentContent.release,
    queryKey: [releaseCacheKey],
    queryFn: skipToken,
    networkMode: "offlineFirst",
    staleTime: Infinity,
  });

  const { data: bundleData } = useQuery<Bundle>({
    enabled: !!currentContent.bundle,
    queryKey: [bundleCacheKey],
    queryFn: skipToken,
    networkMode: "offlineFirst",
    staleTime: Infinity,
  });

  // Prefer data cached in query client
  const cardDataToUse = customCardData ?? currentContent;
  const artistDataToUse = artistData ?? cardDataToUse.artist;
  const comicDataToUse = comicData ?? cardDataToUse.comic;
  const releaseDataToUse = releaseData ?? cardDataToUse.release;
  const bundleDataToUse = bundleData ?? cardDataToUse.bundle;

  const { handleToggleArtistFollow } = useToggleArtistFollow(artistCacheKey);
  const handleToggleComicFollow = useToggleComicFollow(
    comicCacheKey,
    true,
  ).handleToggleFollow;

  const isEntityTypeArtist =
    cardDataToUse.context_entity_type_id === CONTEXT_ENTITY_TYPES.artist;
  const isEntityTypeComic =
    cardDataToUse.context_entity_type_id === CONTEXT_ENTITY_TYPES.comic;

  const releasePricing = useReleasePricingInfo(releaseDataToUse ?? undefined);
  // Bundle pricing logic - always call hook, it handles null bundles
  const { priceToUse: bundlePrice, hasDiscount } =
    useBundlePricing(bundleDataToUse);
  const buttonState = useMemo(
    () =>
      getButtonState(
        cardDataToUse,
        artistDataToUse,
        comicDataToUse,
        isEntityTypeArtist,
        isEntityTypeComic,
        t,
        releasePricing,
        releaseDataToUse ?? undefined,
        bundleDataToUse ?? undefined,
      ),
    [
      cardDataToUse,
      comicDataToUse,
      artistDataToUse,
      isEntityTypeArtist,
      isEntityTypeComic,
      releaseDataToUse,
      bundleDataToUse,
      releasePricing,
      t,
    ],
  );

  const navigate = useNavigate();
  const accountData = useAccountData();
  const { goToCTAUrl, goToCTAUrlWithContext, navigationContext } =
    useNavigateToReaderWithContext(
    {
      comicToNavigate:
        isEntityTypeComic && comicDataToUse ? comicDataToUse : undefined,
      releaseToNavigate: releaseDataToUse ? releaseDataToUse : undefined,
      section: section,
      cardPosition: index,
      sectionPosition: sectionIndex,
      trigger: NavigationTrigger.heroCardClicked,
    },
    [comicDataToUse],
  );

  const { openReleaseInfoMenu, openBundleDetails } = useSlideInMenu();

  const trackRecsysHeroCardClick = () => {
    if (!section || sectionIndex === undefined) {
      return undefined;
    }

    return trackRecsysCardItemClick({
      section,
      sectionIndex,
      item: cardDataToUse,
      itemIndex: index ?? 0,
    });
  };

  const handleButtonClick = () => {
    const impressionId = trackRecsysHeroCardClick();

    if (releaseDataToUse) {
      if (releasePricing.isUnlocked) {
        if (impressionId) {
          goToCTAUrlWithContext({ impressionId });
        } else {
          goToCTAUrl();
        }
      } else if (releaseDataToUse.key) {
        openReleaseInfoMenu(
          releaseDataToUse.key,
          releaseDataToUse.comic?.slug,
          applyNavigationAnalyticsAttribution(navigationContext, {
            impressionId,
          }),
        );
      }
      return;
    }

    if (bundleDataToUse && bundleDataToUse.uuid) {
      openBundleDetails(bundleDataToUse.uuid);
      return;
    }

    if (cardDataToUse.cta_type === "notify") {
      if (isEntityTypeArtist && artistDataToUse) {
        handleToggleArtistFollow();
      } else if (isEntityTypeComic && comicDataToUse) {
        handleToggleComicFollow();
      }
    } else if (cardDataToUse.cta_type === "deeplink") {
      if (isEntityTypeArtist && artistDataToUse) {
        navigate(artistDataToUse.url);
      } else if (isEntityTypeComic && comicDataToUse) {
        if (impressionId) {
          goToCTAUrlWithContext({ impressionId });
        } else {
          goToCTAUrl();
        }
      }
    } else if (cardDataToUse.cta_type === "url" && cardDataToUse.cta_url) {
      if (cardDataToUse.cta_url.includes("/stream")) {
        const channels = accountData?.home_tab_channels;
        const streams = channels?.flatMap((channel) => channel.streams);
        const urlChunks = cardDataToUse.cta_url?.split("/") ?? [];
        const urlStreamId = urlChunks[urlChunks.length - 1];
        const newActiveChannel = streams?.find((stream) => {
          return urlStreamId === stream.id;
        });

        navigate(`/channel/${newActiveChannel?.slug}`);
      } else if (cardDataToUse.cta_url.includes("news/details")) {
        navigate(new URL(cardDataToUse.cta_url).pathname);
      } else {
        window.location.href = cardDataToUse.cta_url;
      }
    }
  };

  // Decrease the font size when display_name contains a long word
  const displayNameWords = cardDataToUse.display_name.split(" ");
  const hasLongWord = displayNameWords.some((word) => word.length >= 9);
  const displayNameFontSize = hasLongWord
    ? {
        base: "16px",
        sm: "20px",
        lg: "30px",
        "2xl": "33px",
        "3xl": "42px",
        "4xl": "63px",
      }
    : {
        base: "18px",
        sm: "24px",
        lg: "36px",
        "2xl": "40px",
        "3xl": "50px",
        "4xl": "72px",
      };

  const displayNameLineHeight = hasLongWord
    ? {
        base: "1.2",
        sm: "1.2",
        lg: "1.1",
        "2xl": "1",
        "3xl": "1",
        "4xl": "1",
      }
    : {
        base: "1.2",
        sm: "1.2",
        lg: "1.0",
        "2xl": "1.0",
        "3xl": "1.0",
        "4xl": "1.0",
      };

  const buttonSize = useBreakpointValue({
    base: "sm",
    "2xl": "md",
    "3xl": "lg",
  });

  return (
    <Flex
      direction={{ base: "row", lg: "column" }}
      ml={{
        base: "0",
        lg: "36px",
        "2xl": "40px",
        "3xl": "51px",
        "4xl": "76px",
      }}
      mr={{
        base: "0",
        lg: "29px",
        "2xl": "32px",
        "3xl": "41px",
        "4xl": "60px",
      }}
      mb={{
        base: "0",
        lg: "66px",
        "2xl": "72px",
        "3xl": "93px",
        "4xl": "138px",
      }}
      pr="24px"
      alignItems={{ base: "center", lg: "start" }}
      width={
        isLarge
          ? {
              base: "260px",
              sm: "336px",
              md: "632px",
              lg: "204px",
              "2xl": "224px",
              "3xl": "285px",
              "4xl": "424px",
            }
          : "100%"
      }
      minH={{ base: extraPadding && !isLarge ? "131px" : "99px", lg: "auto" }}
      justifyContent={{ base: "space-between", lg: "end" }}
      animation={
        isFadingOut ? `${fadeOut} 0.3s linear` : `${fadeIn} 0.4s linear`
      }
    >
      {isLarge && (
        <Text
          fontSize={{
            base: "15px",
            "2xl": "16px",
            "3xl": "20px",
            "4xl": "32px",
          }}
          fontWeight={700}
          color="blaze.blaze"
          textAlign="left"
          w="100%"
          textTransform="uppercase"
          mb={{
            base: "22px",
            "2xl": "24px",
            "3xl": "31px",
            "4xl": "45px",
          }}
        >
          {cardDataToUse?.eyebrow}
        </Text>
      )}
      <Flex
        direction="column"
        alignSelf={{ base: "center", lg: "flex-start" }}
        gap={releaseDataToUse || bundleDataToUse ? "8px" : "0px"}
        pl={isLarge ? "0" : "8px"}
        maxW={{ base: "55%", sm: "68%", lg: "100%" }}
      >
        <Heading
          as="h2"
          fontSize={displayNameFontSize}
          lineHeight={displayNameLineHeight}
          fontWeight={700}
          color="neutral.white"
          textAlign="left"
          noOfLines={isLarge ? undefined : 2}
          textOverflow="ellipsis"
          mb={
            releaseDataToUse || bundleDataToUse
              ? {}
              : {
                  base: "0",
                  lg: "36px",
                  "2xl": "40px",
                  "3xl": "51px",
                  "4xl": "75px",
                }
          }
          alignSelf="flex-start"
        >
          {cardDataToUse.display_name}
        </Heading>
        {releaseDataToUse && (
          <Box mb={isLarge ? "40px" : "0"} alignSelf="flex-start">
            <ReleasePriceLabel release={releaseDataToUse} compact />
          </Box>
        )}
        {bundleDataToUse && bundlePrice && (
          <Box mb={isLarge ? "40px" : "0"} alignSelf="flex-start">
            <PriceDisplay
              price={bundlePrice}
              originalPrice={hasDiscount ? bundleDataToUse.price : undefined}
              hasDiscount={hasDiscount}
            />
          </Box>
        )}
      </Flex>
      {content.cta_type === "notify" && isEntityTypeComic && comicDataToUse ? (
        <ReadingStatusButton
          width="fit-content"
          cacheKey={`${comicDataToUse.entity_type}-${comicDataToUse.id}`}
          ctaButton={
            <Button
              variant="primary"
              isActive={!!comicDataToUse.library_status}
              onClick={trackRecsysHeroCardClick}
              alignSelf={{ base: "center", lg: "flex-start" }}
              size="md"
              minW={{
                base: "100px",
                xs: "105px",
                md: "130px",
                lg: "140px",
                xl: "162px",
                "3xl": "196px",
              }}
            >
              {comicDataToUse.library_status
                ? t("components.cards.customCard.buttonText.following")
                : t("components.cards.customCard.buttonText.follow")}
            </Button>
          }
        />
      ) : (
        buttonState.text && (
          <Button
            alignSelf={{ base: "center", lg: "flex-start" }}
            onClick={(e) => {
              e.stopPropagation();
              e.preventDefault();
              handleButtonClick();
            }}
            minW={{
              base: "100px",
              xs: "105px",
              md: "130px",
              lg: "140px",
              xl: "162px",
              "3xl": "196px",
            }}
            variant={buttonState.variant}
            height="32px"
            size={buttonSize}
          >
            {buttonState.text}
          </Button>
        )
      )}
    </Flex>
  );
}

interface HeroCardButtonState {
  text: string;
  variant: "primary" | "secondary" | "tertiary";
}

function getButtonState(
  content: ContentSectionCustomCard,
  artist: Artist | null,
  comic: Comic | null,
  isEntityTypeArtist: boolean,
  isEntityTypeComic: boolean,
  t: TFunction,
  releasePricing: ReleasePricingInfo,
  release?: Release,
  bundle?: Bundle,
): HeroCardButtonState {
  const buttonState: HeroCardButtonState = {
    text: "",
    variant: "primary",
  };

  if (release) {
    if (releasePricing.isUnlocked) {
      const canContinue = !!release.user_read_status;
      buttonState.text = t(
        canContinue
          ? "components.cards.customCard.buttonText.continueReading"
          : "components.cards.customCard.buttonText.startReading",
      );
      return buttonState;
    }

    // Only show "Free with Gold" if release is ONLY unlockable with gold (no purchase option)
    const isOnlyFreeWithGold =
      releasePricing.type === ReleasePricingType.FreeWithGold;

    if (isOnlyFreeWithGold) {
      buttonState.text = t(
        "components.cards.customCard.buttonText.freeWithGold",
      );
    } else {
      // Everything else (including PpbOrFreeWithGold, OnSaleOrFreeWithGold, premium releases) shows "Buy Now"
      buttonState.text = t("components.cards.customCard.buttonText.buyNow");
    }
    return buttonState;
  }

  if (bundle) {
    buttonState.text = t("components.cards.customCard.buttonText.buyNow");
    return buttonState;
  }

  if (content.cta_type === "notify") {
    let isFollowing: boolean | undefined = false;

    if (isEntityTypeArtist) {
      isFollowing = artist?.user_is_following;
    } else if (isEntityTypeComic) {
      isFollowing = !!comic?.library_status;
    } else {
      return buttonState;
    }

    if (isFollowing) {
      buttonState.text = t("components.cards.customCard.buttonText.following");
      buttonState.variant = "tertiary";
    } else {
      buttonState.text = t("components.cards.customCard.buttonText.follow");
    }
  } else if (content.cta_type === "deeplink") {
    if (isEntityTypeArtist && artist) {
      buttonState.text = t(
        "components.cards.customCard.buttonText.viewProfile",
      );
    } else if (isEntityTypeComic && comic) {
      if (comic.user_read_status) {
        buttonState.text = t(
          "components.cards.customCard.buttonText.continueReading",
        );
      } else {
        buttonState.text = t(
          "components.cards.customCard.buttonText.startReading",
        );
      }
    }
  } else if (content.cta_type === "url" && content.cta_url) {
    buttonState.text = t("components.cards.customCard.buttonText.learnMore");
  }

  return buttonState;
}
